import React from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { getRoleLabel } from '../utils/roles';

/**
 * Glossary for the Projections page: what the value columns mean and how the
 * player roles are derived. The role rules mirror the warehouse view that
 * classifies players (average batting position, balls per innings as a bowler,
 * and the share of deliveries by phase), so keep both in step.
 */

const METRICS: Array<[string, string]> = [
  ['Where the numbers come from', 'Every ball in the warehouse carries Hawk-Eye expected runs and expected wickets at three moments: before the ball bounces (what the delivery is worth), before the shot (what an average batter would do with it) and after the shot (what this shot was worth). A player\'s value on each ball is the gap between those expectations and what happened. Values are per ball and relative to the average IPL player, so 0 means average.'],
  ['Run value', 'Runs above an average player per ball. For a batter it is shot run value plus the batter run residual (runs actually scored minus the post-shot expectation): everything he produced beyond what an average batter would have scored off the same balls. For a bowler it is runs conceded below expectation.'],
  ['Shot run value', 'The part of a batter\'s run value from shot selection and execution: expected runs after the shot minus expected runs before it, given the ball bowled.'],
  ['Dismissal value', 'Wickets per ball above or below an average player, so it is in wickets, not runs, which is why the numbers are small. For a batter: the dismissals an average batter would have suffered on the same balls minus his own, so positive means he survives more than expected. For a bowler: wickets taken beyond what an average bowler would have taken, so positive means he strikes more often.'],
  ['Total value', 'Run value plus dismissal value converted to runs: total = run value + wicket run value × dismissal value, in runs per ball. The wicket run value is the runs an IPL innings typically loses when a wicket falls in that phase: 8.24 in the powerplay, 7.51 in the middle, 6.76 after the powerplay, 4.87 at the death and 7.2 across all phases.'],
  ['Ball vs bowler (bowlers)', 'Ball values rate each delivery on its tracked attributes before it bounces. Bowler values credit the bowler for the outcome, including what the batter did with it.'],
  ['Per ball / per innings', 'Values are stored per ball. Per innings multiplies every value by the player\'s average balls per innings (the exact figure, shown rounded in the Balls/Inns column), so run and total value become runs per innings and dismissal value becomes wickets per innings. It is a display scale only; it lets a top-order batter and a finisher be compared on volume, but a player who bats few balls is scaled down accordingly.'],
  ['Phase', 'PP is the powerplay (overs 1 to 6), Mid the middle overs (7 to 16), Slog the death (17 to 20), and Post-PP everything after the powerplay (Mid and Slog together). "All" combines every phase; "Combined" combines pace and spin.'],
];

const PHASES: Array<[string, string]> = [['pp1', 'Overs 1 to 3'], ['pp2', 'Overs 4 to 6'], ['mid', 'Overs 7 to 16'], ['slog', 'Overs 17 to 20']];

const BATTING_ROLES: Array<[string, string]> = [
  ['top_order', 'Average batting position 3.5 or better'],
  ['middle_order', 'Average position from 3.5 to under 6'],
  ['lower_order', 'Average position from 6 to under 8'],
  ['tail', 'Average position 8 or lower'],
];

const BOWLING_ROLES: Array<[string, string]> = [
  ['part_time', 'Fewer than 90 deliveries in total'],
  ['pp_mid_spinner', 'Spinner with 30% or more of deliveries in overs 1 to 6'],
  ['middle_spinner', 'Any other spinner'],
  ['pp_specialist', 'Pacer with more than 60% of deliveries in overs 1 to 6'],
  ['top_tail', 'Pacer with at least 20% in overs 1 to 3 and 15% at the death, and those two together 55% or more'],
  ['closer', 'Pacer whose overs 4 to 6 share is at least 25% and at least as large as overs 1 to 3, with overs 4 to 20 making up 70% or more'],
  ['middle_pacer', 'Pacer with 56% or more of deliveries in overs 7 to 16'],
  ['post_pp', 'Pacer with 30% or less of deliveries in overs 1 to 6'],
  ['all_phase', 'Any other pacer'],
];

const OVERALL_ROLES: Array<[string, string]> = [
  ['top_order', 'Bats at 3.5 or better and bowls under 10 balls an innings'],
  ['pace_ar', 'Bats inside 8, bowls 10 or more balls an innings, pace'],
  ['spin_ar', 'Bats inside 8, bowls 10 or more balls an innings, spin'],
  ['all_rounder', 'Bats inside 8, bowls 10 or more balls an innings, other bowling type'],
  ['middle_lower_order', 'Bats inside 8 and bowls under 10 balls an innings'],
  ['pacer', 'Everyone else who bowls pace'],
  ['spinner', 'Everyone else who bowls spin'],
  ['bowler', 'Everyone else'],
];

const roleTable = (rows: Array<[string, string]>, label = 'Role') => (
  <Table size="small" sx={{ mb: 2, '& td, & th': { py: 0.6 } }}>
    <TableHead><TableRow><TableCell sx={{ width: 180 }}>{label}</TableCell><TableCell>Rule</TableCell></TableRow></TableHead>
    <TableBody>{rows.map(([k, v]) => <TableRow key={k}><TableCell sx={{ fontWeight: 600 }}>{getRoleLabel(k)}<Typography variant="caption" color="text.secondary" display="block">{k}</Typography></TableCell><TableCell>{v}</TableCell></TableRow>)}</TableBody>
  </Table>
);

const h = (text: string) => <Typography sx={{ fontWeight: 700, fontSize: 15, mt: 2, mb: 0.75 }}>{text}</Typography>;

const ProjectionsGlossary: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
    <DialogTitle sx={{ fontWeight: 700 }}>Projections glossary</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary">What the value columns mean, and how each player's roles are derived. Roles use T20 matches since the start of the projection window.</Typography>

      {h('Value columns')}
      <Table size="small" sx={{ mb: 1, '& td, & th': { py: 0.6 } }}>
        <TableBody>{METRICS.map(([k, v]) => <TableRow key={k}><TableCell sx={{ fontWeight: 600, width: 180, verticalAlign: 'top' }}>{k}</TableCell><TableCell>{v}</TableCell></TableRow>)}</TableBody>
      </Table>

      {h('Overall role')}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>One label per player from average batting position, balls per innings as a bowler, and bowling type. Rules are checked top to bottom; the first match wins, so a top-order batter who bowls 10 or more balls an innings is an all-rounder.</Typography>
      {roleTable(OVERALL_ROLES)}

      {h('Batting role')}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>From average batting position in playing elevens. Blank for players who have not batted.</Typography>
      {roleTable(BATTING_ROLES)}

      {h('Bowling role')}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>From bowling type and the share of deliveries by phase. Rules are checked top to bottom. Blank for players who have not bowled.</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>{PHASES.map(([k, v]) => <Typography key={k} variant="caption" color="text.secondary"><b>{k}</b> {v}</Typography>)}</Box>
      {roleTable(BOWLING_ROLES)}
    </DialogContent>
    <DialogActions><Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button></DialogActions>
  </Dialog>
);

export default ProjectionsGlossary;
